import { useState } from "react";
import { Eye, FileDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";
import toast from "react-hot-toast";
import { fetchAPIBlob } from "../../config/api";
import { ReportHeader } from "../../components/reports";
import { useDepthChart } from "./hooks/use-depth-chart";
import { BaseballField } from "./components";

export const DepthChartPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { report, positionMap, headshotMap, loading } = useDepthChart(reportId);
  const [exporting, setExporting] = useState(false);

  const buildPayload = () => ({
    positionMap,
    headshotMap,
    report: { teamId: report.teamId, teamName: report.teamName, year: report.year },
  });

  const handlePreview = () => {
    sessionStorage.setItem("depthChartConfig", JSON.stringify(buildPayload()));
    window.open("/render/depth-chart", "_blank");
  };

  const handleExport = async () => {
    if (exporting || !report) return;
    setExporting(true);
    const toastId = toast.loading("Generating PDF...");
    try {
      const blob = await fetchAPIBlob("/reports/depth_chart", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      saveAs(blob, `${report.teamId || "team"}_depth_chart_${report.year}.pdf`);
      toast.success("PDF downloaded!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error("Failed to export PDF", { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
          <button
            onClick={() => navigate("/scouting")}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Scouting
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <ReportHeader
          teamId={report.teamId}
          teamName={report.teamName}
          reportType="Depth Chart"
          year={report.year}
        >
          <button
            onClick={handlePreview}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <Eye size={14} />
            Preview
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FileDown size={14} />
            {exporting ? "Exporting…" : "Export PDF"}
          </button>
        </ReportHeader>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-6">
          <BaseballField positionMap={positionMap} headshotMap={headshotMap} />
        </div>
      </div>
    </div>
  );
};
